import { FC, useEffect, useState } from 'react';
import {Button, Typography, Form} from 'antd';
import classNames from 'classnames';

import { getUserSettings, updateUserSettings } from 'features/userSettings/actions';
import { getLoyalty } from 'features/overview/actions';

import { Skeleton } from 'componets/ui';
import { AccountLayout } from 'componets/layouts';
import { SettingsCard } from './SettingsCard';
import { SettingsDisabledInput } from './SettingsDisabledInput';
import { SettingNotificationCard } from './SettingNotificationCard';
import { SettingLoyaltyCard } from './SettingLoyaltyCard';
import { SettingsLanguageCard } from './SettingsLanguageCard';
import { SettingsResidencyCard } from './SettingsResidencyCard';
import { SettingsReferralCard } from './SettingsReferralCard';
import { SettingsPersonalBonusCard } from './SettingsPersonalBonusCard';
import { SettingsSecurityCard } from './SettingsSecurityCard';
import { SettingsPersonalInformationCard } from './SettingsPersonalInformationCard';
import { SettingsBankAccountCards } from './SettingsBankAccountCards';
import { SettingsDeleteAccount } from './SettingsDeleteAccount';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { FieldData } from 'models';

import {ClientRole, Rules} from 'utils/constants';
import Utils from 'utils/utils';
import { setCurrentLanguage } from 'utils';
import { language } from 'utils/i18n';

import { selectIsAdmin, selectUser } from 'selectors';

import s from './index.module.scss';
import {SettingsAddressCard} from "./SettingsAddressCard";
import DocumentsCard from "./DocumentsCard";
import {FormItem, SelectField, TextField} from "../../../componets/form";
import {useTranslation} from "react-i18next";

const WORLDS = [
	'companyRegistrationNumberText',
	'saveChangesText',
	'companyNameText',
	'companyInformationText',
	'rewardProgramsText',
	'economicActivityText'
];

export const Settings: FC = () => {
	return (
		<AccountLayout>
			<SettingsForm />
		</AccountLayout>
	);
};

const SettingsForm = () => {
	const [form] = Form.useForm();
	const [fields, setFields] = useState<FieldData[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { t } = useTranslation();

	const settings = useAppSelector((state) => state.userSettings.entity);
	const user = useAppSelector(selectUser);
	const isAdmin = useAppSelector(selectIsAdmin);

	const { economicActivity = [] } = useAppSelector((state) => state.settings.globals);

	const {
		companyRegistrationNumberText,
		saveChangesText,
		companyNameText,
		companyInformationText,
		rewardProgramsText,
		economicActivityText
	} = useTranslate(WORLDS);

	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(getUserSettings());
		dispatch(getLoyalty());
	}, [dispatch]);

	const onFinish = (payload: any) => {
		const data = payload;
		if (data?.citizenship_id) {
			data.citizenship_id = Number(data.citizenship_id);
		}

		if (!data?.economic_activity && settings.economic_activity) {
			data.economic_activity = Number(settings.economic_activity);
		} else if (data?.economic_activity) {
			data.economic_activity = Number(data.economic_activity);
		}

		if (Number(data.economic_activity) === 25 && !data?.other_economic_activity &&
			settings.other_economic_activity) {
			data.other_economic_activity = settings.other_economic_activity;
		}
		setIsSubmitting(true);
		dispatch(updateUserSettings({ ...data }))
			.unwrap()
			.then(() => {
				if (data.language !== language) {
					setCurrentLanguage(data.language).then();

					const langPath = window.location.pathname.split('/')[1];

					if (langPath !== data.language) {
						window.location.href = window.location.pathname.replace(langPath, data.language);
					}
				}
			})
			.catch(({ errors }) => {
				Utils.injectFormErrors(fields, errors, setFields);
				Utils.scrollToFirstError(errors, form);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const initialValues = {
		country_id: settings.country_id,
		city: settings.city,
		address: settings.address,
		first_name: settings.first_name,
		last_name: settings.last_name,
		email: settings.email,
		citizenship_id: settings.citizenship_id,
		zip: settings.zip,
		economic_activity: settings.economic_activity,
		birth_date: settings.birth_date,
		language,
		phone: settings.phone,
		other_economic_activity: settings.other_economic_activity,
	};

	const economicActivityOptions = economicActivity.map((item) => ({
		label: t(item.title),
		value: item.id
	}))

	return (
		<Skeleton loading={!settings?.country_id}>
			<div className={s.root}>
				<Form
					encType="multipart/form-data"
					className={s.form}
					form={form}
					onFinish={onFinish}
					onFieldsChange={setFields}
					fields={fields}
					initialValues={initialValues}
					scrollToFirstError={{
						behavior: 'smooth',
						block: 'center',
					}}
				>
					<SettingsPersonalInformationCard userType={user?.userType} />

					{user.userType === ClientRole.COMPANY && (
						<SettingsCard title={companyInformationText}>
							<div className={s.formGrid}>
								<SettingsDisabledInput
									className={s.col}
									name={companyNameText}
									value={settings.company_name}
								/>
								<SettingsDisabledInput
									className={s.col}
									name={companyRegistrationNumberText}
									value={settings.registration_number}
								/>
							</div>
							<div className={s.formGrid} style={{ marginTop: 32 }}>
								{Number(settings.economic_activity) === 25 ? (
									<SettingsDisabledInput
										name={economicActivityText}
										value={settings.other_economic_activity}
									/>
								) : (
									<>
										{Boolean(settings.economic_activity) ? (
											<SettingsDisabledInput
												name={economicActivityText}
												options={economicActivityOptions}
												value={settings.economic_activity}
											/>
										) : (
											<>
												<FormItem
													className={s.mb0}
													name="economic_activity"
													rules={[Rules.REQUIRED_SELECT]}
												>
													<SelectField
														options={economicActivityOptions}
														placeholder={economicActivityText}
														isFilter
													/>
												</FormItem>
												{Number(form.getFieldValue('economic_activity')) === 25 && (
													<FormItem
														name="other_economic_activity"
														rules={[
															Rules.REQUIRED,
															Rules.MAX_LENGTH('other_economic_activity', 48),
														]}
													>
														<TextField
															disabled={Boolean(initialValues?.other_economic_activity)}
															label={t('economicActivityOther')}
														/>
													</FormItem>
												)}
											</>
										)}
									</>
								)}
							</div>
							<div className={s.formGrid} style={{ marginTop: 32 }}>
								<SettingsAddressCard />
							</div>
						</SettingsCard>
					)}

					<DocumentsCard />

					<SettingsLanguageCard />

					<div>
						<Button
							type="primary"
							htmlType="submit"
							className={classNames(s.submitBtn, { 'ant-btn-grey': isAdmin })}
							loading={isSubmitting}
						>
							{saveChangesText}
						</Button>
					</div>
				</Form>

				<div className={s.reward}>
					<SettingsResidencyCard />

					{user.accounts?.length > 0 && <SettingsBankAccountCards />}

					<SettingNotificationCard />

					<SettingsSecurityCard />
				</div>

				<div className={s.reward}>
					<Typography.Title level={4}>{rewardProgramsText} 🏆</Typography.Title>

					{(user.personalInterests.length > 0 ||
						(!user.category?.isDefault &&
							!user.category?.isLoyalty &&
							+user.category?.interestRate > 0)) && <SettingsPersonalBonusCard />}

					<SettingLoyaltyCard />

					<SettingsReferralCard />
				</div>

				<SettingsDeleteAccount />
			</div>
		</Skeleton>
	);
};
